/**
 * CurrencyFlag — maps ISO 4217 currency codes to ISO 3166-1 alpha-2 country codes
 * and renders flag images via flagcdn.com CDN. Works on ALL platforms.
 */
import React from 'react';
import { FlagImg } from './CountryFlag';

/** Maps currency code → ISO2 country code for flagcdn.com */
export const CURRENCY_ISO2: Record<string, string> = {
  EUR: 'eu', GBP: 'gb', USD: 'us', CHF: 'ch', PLN: 'pl',
  SEK: 'se', NOK: 'no', DKK: 'dk', CZK: 'cz', HUF: 'hu',
  RON: 'ro', AED: 'ae', SAR: 'sa', ZAR: 'za',
  CAD: 'ca', AUD: 'au', NZD: 'nz', SGD: 'sg', HKD: 'hk',
  JPY: 'jp', CNY: 'cn', INR: 'in', MXN: 'mx', BRL: 'br',
  TRY: 'tr', ILS: 'il', THB: 'th', KES: 'ke', HRK: 'hr',
  BHD: 'bh', ISK: 'is', HRV: 'hr', ALL: 'al', BAM: 'ba',
  MKD: 'mk', RSD: 'rs', UAH: 'ua', GEL: 'ge', AMD: 'am',
  AZN: 'az', KZT: 'kz', UZS: 'uz', MDL: 'md', BYN: 'by',
  TND: 'tn', MAD: 'ma', EGP: 'eg', NGN: 'ng', GHS: 'gh',
  UGX: 'ug', TZS: 'tz', ETB: 'et', RWF: 'rw', ZMW: 'zm',
  MUR: 'mu', BWP: 'bw', MZN: 'mz', NAD: 'na', SZL: 'sz',
  XOF: 'sn', XAF: 'cm', PKR: 'pk', BDT: 'bd', LKR: 'lk',
  NPR: 'np', MMK: 'mm', VND: 'vn', IDR: 'id', MYR: 'my',
  PHP: 'ph', KRW: 'kr', TWD: 'tw', PEN: 'pe', CLP: 'cl',
  COP: 'co', ARS: 'ar', BOB: 'bo', PYG: 'py', UYU: 'uy',
  VEF: 've', CRC: 'cr', GTQ: 'gt', HNL: 'hn', NIO: 'ni',
  PAB: 'pa', DOP: 'do', JMD: 'jm', TTD: 'tt', BBD: 'bb',
  BZD: 'bz', GYD: 'gy', SRD: 'sr', QAR: 'qa', KWD: 'kw',
  BHD_: 'bh', OMR: 'om', JOD: 'jo', LBP: 'lb', IQD: 'iq',
  YER: 'ye', IRR: 'ir', AFN: 'af', MVR: 'mv', BTN: 'bt',
};

interface CurrencyFlagProps {
  currency: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/**
 * Renders a country flag for a currency code. Uses flagcdn.com (image-based).
 * Falls back to a generic currency icon if no mapping found.
 */
export const CurrencyFlag = React.forwardRef<HTMLElement, CurrencyFlagProps>(
  function CurrencyFlag({ currency, size = 'sm', className }, ref) {
    const ccy = currency?.toUpperCase?.() || '';
    const iso2 = CURRENCY_ISO2[ccy];

    if (!iso2) {
      return (
        <span
          ref={ref as React.Ref<HTMLSpanElement>}
          className={`inline-flex items-center justify-center rounded-full bg-secondary/60 text-xs font-bold text-muted-foreground ring-1 ring-border/40 ${className ?? ''}`}
        >
          {ccy || '—'}
        </span>
      );
    }

    if (size === 'xl') {
      return (
        <FlagImg
          ref={ref as React.Ref<HTMLImageElement>}
          code={iso2}
          size="xl"
          className={`block w-full h-full rounded-full ${className ?? ''}`}
        />
      );
    }

    return <FlagImg ref={ref as React.Ref<HTMLImageElement>} code={iso2} size={size} className={className} />;
  }
);

CurrencyFlag.displayName = 'CurrencyFlag';
